import React, { useEffect, useState } from 'react'
import { MenuItem, Stack, TextField, Typography } from '@mui/material'
import { SmartDialog } from '../../generic/utilities/SmartDialog'
import { compareBomVersions, readBomLibraryError } from './bomLibraryApi'
import { translateBomLibraryError } from './bomLibraryErrors'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import BomComparisonView from './BomComparisonView'

/**
 * BomCompareDialog — S52 (historia 117 HU-04, RN-04.14/D21). By default compares against the
 * immediately previous version (no `fromBomId` sent, Sun resolves n-1); the user can pick any other
 * version of the same code as the reference. Comparing is a read-only query (only needs
 * BOM_LIBRARY): it never mutates anything.
 * @param {number} props.toBomId The version being compared (bomId)
 * @param {Array<{bomId:number, versionNumber:number}>} props.versions All versions of the same code (for the reference selector)
 */
const BomCompareDialog = ({ toBomId, versions, onClose }) => {
  const { t_ } = useSmartTranslation()
  const [fromBomId, setFromBomId] = useState('')
  const [comparison, setComparison] = useState(null)
  const [errorMessage, setErrorMessage] = useState('')

  // A rejected comparison (Sun answers 404 `BOM version not found` when there is no n-1 to compare
  // against, E-BOM-15 across BOMs...) is shown inside the dialog instead of surfacing as an
  // unhandled rejection.
  const runCompare = async (body) => {
    setErrorMessage('')
    try {
      setComparison(await compareBomVersions(body))
    } catch (thrown) {
      setComparison(null)
      setErrorMessage(readBomLibraryError(thrown).error || 'Not allowed')
    }
  }

  useEffect(() => {
    runCompare({ toBomId })
    // Runs once per toBomId: the default reference (n-1) is only requested on open, not on
    // every reference-selector change (that path calls compareBomVersions explicitly below).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toBomId])

  const handleSelectReference = (event) => {
    const selected = event.target.value
    setFromBomId(selected)
    runCompare({ fromBomId: selected || undefined, toBomId })
  }

  const referenceOptions = (versions || []).filter((version) => version.bomId !== toBomId)

  return (
    <SmartDialog
      setOpen
      title={{ render: <Typography variant='h6'>{t_('Compare')}</Typography> }}
      accept={false}
      cancel={false}
      close
      closeCallback={onClose}
      paperWidth='60rem'
      renderComponent={
        <Stack spacing={2} sx={{ textAlign: 'left' }} data-testid='bom-compare-dialog'>
          <TextField
            select
            label={t_('Compare with')}
            value={fromBomId}
            onChange={handleSelectReference}
            sx={{ minWidth: 240 }}
          >
            <MenuItem value=''>{t_('Previous version')}</MenuItem>
            {referenceOptions.map((version) => (
              <MenuItem key={version.bomId} value={version.bomId}>{t_('Version') + ' ' + version.versionNumber}</MenuItem>
            ))}
          </TextField>
          {errorMessage ? <Typography color='error.main' data-testid='bom-compare-error'>{translateBomLibraryError(t_, errorMessage)}</Typography> : null}
          <BomComparisonView comparison={comparison} />
        </Stack>
      }
    />
  )
}

export default BomCompareDialog
